import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Dialog, Tab, Tabs, TextField, } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stateManagement/store';
import { UpdateUserDto } from "@ontime/types";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import UserUpdatePassword from './UserUpdatePassword';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import { useDispatch } from 'react-redux';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import { PhoneCountryCode } from "@ontime/types";

const userSchema = z.object({
    name: z.string().min(2, "First name must be at least 2 characters").max(50),
    surname: z.string().min(2, "Last name must be at least 2 characters").max(50),
    userName: z.string().min(4, "Username must be at least 4 characters").max(50),
    emailAddress: z.string().email("Invalid email format"),
    phoneCountryCode: z.nativeEnum(PhoneCountryCode),
    phoneNumber: z.string().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number format"),
});

const defaultValues: UpdateUserDto = {
    id: 0,
    userName: '',
    name: '',
    surname: '',
    emailAddress: '',
    phoneCountryCode: 1,
    phoneNumber: '',
};

const UserDetailDialog: React.FC<{
    state: { open: boolean; };
    closeUserDialog: () => void;
}> = ({ state, closeUserDialog }) => {

    const auth = useSelector((state: RootState) => state.auth);

    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch<AppDispatch>();

    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<UpdateUserDto>({
        resolver: zodResolver(userSchema),
        defaultValues: defaultValues
    });

    const handleClose = () => {
        reset();
        closeUserDialog();
    };

    const handleUpdate = async (data: UpdateUserDto) => {
        // setLoading(true);

        // try {
        //     const response = await UserService.UpdateByUser({ ...data, id: auth.token?.userId });
        //     if (response.response.success) {
        //         enqueueSnackbar('User details updated successfully.', { variant: 'success' });
        //         dispatch(updateUserName({ fullName: data.name + ' ' + data.surname, userName: data.userName }))
        //     } else {
        //         enqueueSnackbar(response.response.error?.message, { variant: 'error' });
        //     }
        // } catch (error) {
        //     console.error(error, { variant: 'error' });
        // } finally {
        //     setLoading(false);
        // }
    };

    const get = async () => {
        // setLoading(true);
        // try {
        //     const response = await UserService.Get(auth.token?.userId ?? 0);
        //     reset({
        //         id: response.data.result.id,
        //         userName: response.data.result.userName,
        //         name: response.data.result.name,
        //         surname: response.data.result.surname,
        //         emailAddress: response.data.result.emailAddress,
        //         phoneCountryCode: response.data.result.phoneCountryCode,
        //         phoneNumber: response.data.result.phoneNumber,
        //     });
        // } catch (error) {
        //     console.error(error, { variant: 'error' });
        // } finally {
        //     setLoading(false);
        // }
    };

    useEffect(() => {

        if (state.open) {
            get();
        }
        else if (!state.open) {
            reset(defaultValues);
        }
    }, [state.open]);

    const [tabId, setTabId] = React.useState<'userInfo' | 'userPass'>('userInfo');

    const handleChange = (event: React.SyntheticEvent, newValue: 'userInfo' | 'userPass') => {
        setTabId(newValue);
    };

    return (
        <Dialog
            open={state.open}
            onClose={() => handleClose()}
            fullWidth
        >
            <CustomDialogTitle handleClose={handleClose} title={auth?.email ?? ''} loading={loading} />
            <Tabs value={tabId} variant="fullWidth" onChange={handleChange}>
                <Tab sx={{ fontSize: '0.9rem', textTransform: 'none' }} label="Profile Info" value='userInfo' />
                <Tab sx={{ fontSize: '0.9rem', textTransform: 'none' }} label="Change Password" value='userPass' />
            </Tabs>
            <CustomDialogContent>
                {tabId === 'userInfo' ?
                    <form onSubmit={handleSubmit(handleUpdate)} noValidate>
                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="First Name"
                                InputLabelProps={{ shrink: true }}
                                {...register('name')}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Last Name"
                                InputLabelProps={{ shrink: true }}
                                {...register('surname')}
                                error={!!errors.surname}
                                helperText={errors.surname?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Email"
                                InputLabelProps={{ shrink: true }}
                                {...register('emailAddress')}
                                error={!!errors.emailAddress}
                                helperText={errors.emailAddress?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <Autocomplete
                                fullWidth
                                options={['', ...Object.values(PhoneCountryCode).filter(v => typeof v === 'number')] as (PhoneCountryCode | '')[]}
                                getOptionLabel={(option) => {
                                    if (option === '') return '';
                                    const countryName = PhoneCountryCode[option as number];
                                    return `+${option} (${countryName})`;
                                }}
                                value={watch('phoneCountryCode') ?? ''}
                                disableClearable
                                onChange={(_, newValue) =>
                                    setValue('phoneCountryCode', newValue as PhoneCountryCode)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Phone Country Code"
                                        InputLabelProps={{ shrink: true }}
                                        error={!!errors.phoneCountryCode}
                                        helperText={errors.phoneCountryCode?.message ? 'Country code is required' : ''}
                                    />
                                )}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Phone Number"
                                InputLabelProps={{ shrink: true }}
                                {...register('phoneNumber')}
                                error={!!errors.phoneNumber}
                                helperText={errors.phoneNumber?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Username"
                                InputLabelProps={{ shrink: true }}
                                {...register('userName')}
                                error={!!errors.userName}
                                helperText={errors.userName?.message}
                            />
                        </Box>

                        <CustomFormAction>
                            <CustomButton
                                lable='Update'
                                type='submit'
                                disabled={loading}
                                infoContent={`Update profile information`}
                            />
                        </CustomFormAction>
                    </form>
                    :
                    <UserUpdatePassword />}
            </CustomDialogContent>
        </Dialog>
    );
};

export default UserDetailDialog;